import type { DraftData } from './DraftTypes';
import { draftManager } from './DraftManager';
import { getUserStorageKey } from '@/hooks/campaign/utils/storageUtils';

/**
 * Legacy DraftService - now delegates to DraftManager
 * Maintained for backward compatibility
 */
export class DraftService {
  generateDraftId(): string {
    return `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  listDrafts(): DraftData[] {
    return draftManager.listDrafts();
  }

  getDraft(id: string): DraftData | null {
    return draftManager.getDraft(id);
  }

  createDraft(name: string): DraftData {
    return draftManager.createDraft(name);
  }

  saveDraft(draftData: DraftData): void {
    draftManager.saveDraft(draftData, true); // Immediate save for legacy compatibility
  }

  deleteDraft(id: string): void {
    draftManager.deleteDraft(id);
  }

  getCurrentDraftId(): string | null {
    return draftManager.getCurrentDraftId();
  }

  setCurrentDraftId(id: string): void {
    draftManager.setCurrentDraft(id);
  }

  loadDraftData(draftId: string): boolean {
    return draftManager.loadDraftToSession(draftId);
  }

  collectCurrentCampaignData(): Partial<DraftData> {
    return {
      currentStep: this.getLocalStorageValue('currentStep', 0),
      selectedLocations: this.getLocalStorageValue(
        getUserStorageKey('selectedLocations'),
        this.getLocalStorageValue('selectedLocations', [])
      ),
      formData: this.getLocalStorageValue('formData', {}),
      duration: this.getLocalStorageValue('duration', 14),
      devices: this.getLocalStorageValue('devices', 0),
      creativeUrls: this.getLocalStorageValue('creativeUrls', []),
      startDate: this.getLocalStorageValue('startDate', ''),
      endDate: this.getLocalStorageValue('endDate', ''),
      budgetCalculations: this.getLocalStorageValue('budgetCalculations', {}),
      locationCreatives: this.getLocalStorageValue('locationCreatives', {}),
      mediaOwnerCreatives: this.getLocalStorageValue('mediaOwnerCreatives', {}),
      useLocationSpecificCreatives: this.getLocalStorageValue(
        getUserStorageKey('useLocationSpecificCreatives'),
        this.getLocalStorageValue('useLocationSpecificCreatives', false)
      ),
      useMediaOwnerSpecificCreatives: this.getLocalStorageValue(
        getUserStorageKey('useMediaOwnerSpecificCreatives'),
        this.getLocalStorageValue('useMediaOwnerSpecificCreatives', false)
      )
    } as Partial<DraftData>;
  }

  private getLocalStorageValue(key: string, defaultValue: any): any {
    try {
      const value = localStorage.getItem(key);
      if (!value || value === 'null' || value === 'undefined') return defaultValue;

      if (key.includes('Creatives') || key.includes('Locations') || key === 'formData' || key === 'budgetCalculations') {
        return JSON.parse(value);
      }
      if (key.includes('use') && key.includes('Creatives')) {
        return value === 'true';
      }
      if (key === 'currentStep' || key === 'duration' || key === 'devices') {
        return parseInt(value, 10) || defaultValue;
      }
      if (key === 'creativeUrls') {
        return JSON.parse(value);
      }

      return value;
    } catch (e) {
      console.error(`Error getting localStorage value for ${key}:`, e);
      return defaultValue;
    }
  }

  clearAllCampaignData(): void {
    draftManager.clearCurrentDraft();
  }
}

export const draftService = new DraftService();
